import React, { useState, useEffect, useRef, forwardRef } from "react";
import { Storage } from "aws-amplify";

const allowedImageExtensions = ["png", "jpeg", "jpg", "gif"];
const allowedVideoExtensions = ["mp4"];

// Simple cache to avoid duplicate fetches in the same session
const mediaUrlCache = {};

const MediaFetcher = forwardRef(
  ({ mediaKey, height, width, objectFit, maxHeight }, ref) => {
    const [mediaUrl, setMediaUrl] = useState(null);
    const [mediaType, setMediaType] = useState(null);
    const [error, setError] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
      let isMounted = true;

      const fetchMedia = async () => {
        try {
          if (mediaUrlCache[mediaKey]) {
            if (isMounted) {
              setMediaUrl(mediaUrlCache[mediaKey].url);
              setMediaType(mediaUrlCache[mediaKey].type);
            }
            return;
          }

          const url = await Storage.get(mediaKey, { expires: 600000 });

          if (!isMounted) return; // Exit if component unmounted during async operation

          const extension = mediaKey
            .split(".")
            .pop()
            .toLowerCase();

          if (allowedImageExtensions.includes(extension)) {
            if (isMounted) {
              setMediaUrl(url);
              setMediaType("image");
              mediaUrlCache[mediaKey] = { url, type: "image" };
            }
          } else if (allowedVideoExtensions.includes(extension)) {
            if (isMounted) {
              setMediaUrl(url);
              setMediaType("video");
              mediaUrlCache[mediaKey] = { url, type: "video" };
            }
          } else {
            if (isMounted) {
              setError(`Unsupported media format: ${extension}`);
            }
          }
        } catch (error) {
          if (isMounted) {
            setError("Error fetching media");
          }
        }
      };

      if (mediaKey) {
        fetchMedia();
      }

      return () => {
        isMounted = false;
      };
    }, [mediaKey]);

    useEffect(() => {
      if (ref && mediaType === "video" && videoRef.current) {
        if (typeof ref === 'function') {
          ref(videoRef.current);
        } else {
          ref.current = videoRef.current;
        }
      }

      return () => {
        // Cleanup ref on unmount to prevent memory leaks
        if (ref && typeof ref !== 'function' && ref.current) {
          ref.current = null;
        }
      };
    }, [ref, mediaType]);

    if (error) {
      return <div>{error}</div>;
    }

    return (
      <>
        {mediaType === "image" && (
          <img
            src={mediaUrl}
            alt="Media"
            style={{ height, width, objectFit, maxHeight }}
          />
        )}
        {mediaType === "video" && (
          <video
            ref={videoRef}
            src={mediaUrl}
            controls
            style={{ height, width, objectFit, maxHeight }}
          />
        )}
      </>
    );
  }
);

export default MediaFetcher;
